/**
 * Admin Ministry Edit Page: create or edit a ministry.
 * Native mobile app style with sections.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconUsers,
  IconAlertTriangle,
  IconTrash,
  IconSearch,
  IconUser,
  IconCamera,
  IconPhoto,
} from "../../../components/Icons.tsx";
import {
  getTelegram,
  haptic,
  safeShowConfirm,
  safeShowAlert,
} from "../../../lib/telegram.ts";
import { useAuth } from "../../../lib/auth.tsx";
import { getAuthHeaders, getErrorMessage } from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";
import {
  useMinistry,
  useCreateMinistry,
  useUpdateMinistry,
  useDeleteMinistry,
  useAddMinistryMember,
  useUpdateMinistryMember,
  useRemoveMinistryMember,
} from "./hooks";
import { useSearchAccounts } from "../home-groups/hooks";
import { useDebouncedValue } from "../../../lib/use-debounce.ts";
import { FullscreenTextEditor } from "../events/components/FullscreenTextEditor.tsx";
import {
  SectionLabel,
  InlineTextField,
  InlineTextPreview,
  InlineStepper,
  InlineToggle,
  InlinePickerRow,
  ExpandablePicker,
} from "../events/components/InlineFields.tsx";
import { formatAccountName } from "../home-groups/types.ts";
import type { AdminAccountRef } from "../home-groups/types.ts";
import {
  type MinistryRole,
  ministryRoleLabel,
  MINISTRY_ROLE_OPTIONS,
} from "./types.ts";


interface MinistryFormData {
  code: string;
  name: string;
  description: string;
  imageUrl: string;
  isActive: boolean;
  sortOrder: number;
}

const emptyForm: MinistryFormData = {
  code: "",
  name: "",
  description: "",
  imageUrl: "",
  isActive: true,
  sortOrder: 0,
};

interface MinistryMemberRow {
  id: string;
  account: AdminAccountRef;
  role: MinistryRole;
  isActive: boolean;
}

export function AdminMinistryEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();
  useAuth();
  const isNew = id === "new";

  // URQL hooks
  const {
    ministry,
    isLoading: isLoadingMinistry,
    error: ministryError,
    refetch: refetchMinistry,
  } = useMinistry(isNew ? undefined : id);
  const { createMinistry, isLoading: isCreating } = useCreateMinistry();
  const { updateMinistry, isLoading: isUpdating } = useUpdateMinistry();
  const { deleteMinistry, isLoading: isDeletingMinistry } = useDeleteMinistry();
  const { addMember } = useAddMinistryMember();
  const { updateMemberRole } = useUpdateMinistryMember();
  const { removeMember } = useRemoveMinistryMember();

  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<MinistryFormData>(emptyForm);
  const [members, setMembers] = useState<MinistryMemberRow[]>([]);
  const [editingField, setEditingField] = useState<"description" | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [rolePickerMemberId, setRolePickerMemberId] = useState<string | null>(
    null,
  );
  const [addMemberRole, setAddMemberRole] = useState<MinistryRole>("MEMBER");
  const [showAddRolePicker, setShowAddRolePicker] = useState(false);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const memberSearchInputRef = useRef<HTMLInputElement>(null);

  const debouncedMemberSearch = useDebouncedValue(memberSearchQuery, 300);

  // Use search accounts hook
  const {
    accounts: memberSearchResults,
    isLoading: isSearchingMembers,
  } = useSearchAccounts({
    query: debouncedMemberSearch,
    limit: 15,
  });

  // Loading and saving states
  const isLoading = isNew ? false : isLoadingMinistry;
  const isSaving = isCreating || isUpdating;
  const isDeleting = isDeletingMinistry;

  // Sync ministry data to form
  useEffect(() => {
    if (ministry) {
      setForm({
        code: ministry.code,
        name: ministry.name,
        description: ministry.description ?? "",
        imageUrl: ministry.imageUrl ?? "",
        isActive: ministry.isActive,
        sortOrder: ministry.sortOrder,
      });
      setMembers(
        (ministry.members ?? []).map((mem) => ({
          id: mem.id,
          account: mem.account,
          role: mem.role as MinistryRole,
          isActive: mem.isActive,
        })),
      );
    }
  }, [ministry]);

  // Sync ministry error
  useEffect(() => {
    if (ministryError) {
      setError(ministryError.message || "Служение не найдено");
    }
  }, [ministryError]);

  // Refocus input after search results render
  useEffect(() => {
    if (debouncedMemberSearch.length >= 2 && memberSearchResults.length > 0) {
      const focusInput = () => memberSearchInputRef.current?.focus();
      requestAnimationFrame(() => {
        setTimeout(focusInput, 50);
      });
    }
  }, [debouncedMemberSearch, memberSearchResults]);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/admin/ministries");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, isNew]);

  const updateForm = useCallback(
    <K extends keyof MinistryFormData>(key: K, value: MinistryFormData[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      safeShowAlert("Введите название служения");
      return;
    }
    if (isNew) {
      const code = form.code.trim().toLowerCase();
      if (!code) {
        safeShowAlert(
          "Введите код служения (латиница, цифры, дефис, подчёркивание)",
        );
        return;
      }
    }
    setError(null);
    try {
      if (isNew) {
        const input = {
          code: form.code.trim().toLowerCase(),
          name,
          description: form.description.trim() || null,
          imageUrl: form.imageUrl.trim() || null,
          isActive: form.isActive,
          sortOrder: form.sortOrder,
        };
        const newId = await createMinistry(input);
        haptic("success");
        navigate(`/admin/ministries/${newId}`, {
          replace: true,
        });
      } else {
        if (!id) {
          setError("ID служения не найден");
          return;
        }
        const input = {
          name,
          description: form.description.trim() || null,
          imageUrl: form.imageUrl.trim() || null,
          isActive: form.isActive,
          sortOrder: form.sortOrder,
        };
        await updateMinistry(id, input);
        haptic("success");
        refetchMinistry();
      }
    } catch (err) {
      setError(getErrorMessage(err));
      haptic("error");
    }
  };

  const handleDelete = () => {
    if (isNew || !id) return;
    safeShowConfirm(
      "Удалить служение? Сначала нужно снять привязки к мероприятиям и удалить участников.",
      async (ok) => {
        if (!ok) return;
        setError(null);
        try {
          await deleteMinistry(id);
          haptic("success");
          navigate("/admin/ministries");
        } catch (err) {
          setError(getErrorMessage(err));
          haptic("error");
        }
      },
    );
  };

  const handleAddMember = async (account: AdminAccountRef) => {
    if (!id || isNew) return;
    try {
      const result = await addMember(id, {
        accountId: account.id,
        role: addMemberRole,
      });
      setMembers((prev) => [
        ...prev,
        {
          id: result.id,
          account: result.account,
          role: result.role,
          isActive: result.isActive,
        },
      ]);
      setMemberSearchQuery("");
      haptic("success");
    } catch (err) {
      safeShowAlert(getErrorMessage(err));
    }
  };

  const handleUpdateMemberRole = async (
    memberId: string,
    role: MinistryRole,
  ) => {
    try {
      await updateMemberRole(memberId, role);
      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role } : m)),
      );
      setRolePickerMemberId(null);
      haptic("success");
    } catch (err) {
      safeShowAlert(getErrorMessage(err));
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    haptic("light");
    try {
      await removeMember(memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      haptic("success");
    } catch (err) {
      safeShowAlert(getErrorMessage(err));
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!id || isNew) return;
    setIsUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/ministry-image/${id}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(response, "ministry image upload");
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = await response.json() as { url: string };
      updateForm("imageUrl", result.url);
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  if (isLoading) return <PageLoader message="Загрузка..." />;

  if (error && !isNew) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Ошибка</h2>
          <p className="text-sm text-slate-500 mb-4">{error}</p>
          <button onClick={refetchMinistry} className="btn-primary">
            Повторить
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 flex-shrink-0">
            <IconUsers className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">
              {isNew ? "Новое служение" : form.name || "Служение"}
            </h1>
            <p className="text-[13px] text-slate-500">
              {isNew ? "Создание служения" : "Редактирование"}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-sm">
            {error}
          </div>
        )}

        <SectionLabel>Основное</SectionLabel>
        <div className="card overflow-hidden mb-5">
          {isNew ? (
            <InlineTextField
              label="Код"
              value={form.code}
              onChange={(v) => updateForm("code", v)}
              placeholder="worship"
              required
            />
          ) : (
            <InlineTextField
              label="Код"
              value={form.code}
              onChange={() => {}}
              disabled
              mono
            />
          )}
          <InlineTextField
            label="Название"
            value={form.name}
            onChange={(v) => updateForm("name", v)}
            placeholder="Название служения"
            required
          />
          <InlineTextPreview
            label="Описание"
            value={form.description}
            placeholder="Краткое описание..."
            onClick={() => setEditingField("description")}
          />
          {/* Image upload (only for existing ministry) */}
          <div className="p-4 border-b border-slate-100">
            <p className="text-[13px] text-slate-500 mb-3">Обложка</p>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleImageUpload(file);
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                if (!isNew && !isUploadingImage) imageInputRef.current?.click();
              }}
              disabled={isNew || isUploadingImage}
              className="relative w-full aspect-[2/1] rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 active:border-slate-300 transition-colors disabled:opacity-90 flex items-center justify-center"
            >
              {form.imageUrl && !isUploadingImage ? (
                <>
                  <img
                    src={
                      imageCacheBust
                        ? `${form.imageUrl}${form.imageUrl.includes("?") ? "&" : "?"}_t=${imageCacheBust}`
                        : form.imageUrl
                    }
                    alt="Обложка"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-black/50 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                    <IconCamera className="w-4 h-4" />
                    Заменить
                  </span>
                </>
              ) : isUploadingImage ? (
                <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  <span className="text-[13px]">Загрузка…</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                  <IconPhoto className="w-10 h-10" />
                  <span className="text-[13px]">
                    {isNew
                      ? "Сохраните служение, чтобы добавить обложку"
                      : "Добавить обложку"}
                  </span>
                </div>
              )}
            </button>
            {form.imageUrl && !isUploadingImage && !isNew && (
              <button
                type="button"
                onClick={() => updateForm("imageUrl", "")}
                className="mt-2 flex items-center gap-1 text-[13px] text-red-500 active:text-red-600"
              >
                <IconTrash className="w-4 h-4" />
                Удалить
              </button>
            )}
          </div>
          <InlineStepper
            label="Порядок"
            value={form.sortOrder}
            onChange={(v) => updateForm("sortOrder", v ?? 0)}
            min={0}
            placeholder="0"
          />
          <InlineToggle
            label="Активно"
            checked={form.isActive}
            onChange={(v) => updateForm("isActive", v)}
          />
        </div>

        <SectionLabel>Служители</SectionLabel>
        <div className="card overflow-hidden mb-5">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between min-h-[52px] px-4 py-3 border-b border-slate-100 last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-slate-900 truncate">
                  {formatAccountName(m.account)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    haptic("light");
                    setRolePickerMemberId(
                      rolePickerMemberId === m.id ? null : m.id,
                    );
                  }}
                  className="text-[13px] text-slate-500 active:text-[var(--color-accent)]"
                >
                  {ministryRoleLabel(m.role)}
                </button>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveMember(m.id)}
                className="p-2 rounded-lg text-slate-400 active:bg-slate-200 active:text-red-600 flex-shrink-0"
                aria-label="Удалить"
              >
                <IconTrash className="w-4 h-4" />
              </button>
            </div>
          ))}
          {rolePickerMemberId &&
            (() => {
              const m = members.find((x) => x.id === rolePickerMemberId);
              if (!m) return null;
              return (
                <div className="mx-3 mb-2">
                  <ExpandablePicker
                    isOpen={true}
                    options={MINISTRY_ROLE_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    value={m.role}
                    onChange={(role) => handleUpdateMemberRole(m.id, role)}
                    onClose={() => setRolePickerMemberId(null)}
                  />
                </div>
              );
            })()}
          {!isNew && (
            <div className="px-4 py-3 border-t border-slate-100">
              <p className="text-[13px] text-slate-500 mb-3">
                Добавить служителя
              </p>
              <div className="space-y-3">
                <InlinePickerRow
                  label="Роль"
                  value={
                    MINISTRY_ROLE_OPTIONS.find((o) => o.value === addMemberRole)
                      ?.label ?? ministryRoleLabel(addMemberRole)
                  }
                  onClick={() => setShowAddRolePicker(!showAddRolePicker)}
                  isOpen={showAddRolePicker}
                />
                {showAddRolePicker && (
                  <ExpandablePicker
                    isOpen={true}
                    options={MINISTRY_ROLE_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.label,
                    }))}
                    value={addMemberRole}
                    onChange={(v) => {
                      setAddMemberRole(v);
                      setShowAddRolePicker(false);
                    }}
                    onClose={() => setShowAddRolePicker(false)}
                  />
                )}
                <div>
                  <div className="relative mb-2">
                    <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      ref={memberSearchInputRef}
                      type="text"
                      value={memberSearchQuery}
                      onChange={(e) => setMemberSearchQuery(e.target.value)}
                      placeholder="Введите имя или @username..."
                      className="w-full pl-9 pr-9 py-2.5 text-[14px] bg-slate-50 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent"
                      autoComplete="off"
                    />
                    {memberSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setMemberSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm leading-none active:opacity-80"
                        aria-label="Очистить"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="max-h-[220px] overflow-y-auto -mx-1 px-1">
                    {!memberSearchQuery && (
                      <p className="text-[14px] text-slate-400 text-center py-4">
                        Введите имя или @username для поиска
                      </p>
                    )}
                    {memberSearchQuery.length > 0 &&
                      memberSearchQuery.length < 2 && (
                        <p className="text-[14px] text-slate-400 text-center py-4">
                          Введите минимум 2 символа
                        </p>
                      )}
                    {memberSearchQuery.length >= 2 && isSearchingMembers && (
                      <div className="flex items-center justify-center gap-2 py-6 text-slate-500">
                        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                        <span className="text-[14px]">Поиск...</span>
                      </div>
                    )}
                    {memberSearchQuery.length >= 2 &&
                      !isSearchingMembers &&
                      memberSearchResults.filter(
                        (acc) => !members.some((m) => m.account.id === acc.id),
                      ).length === 0 && (
                        <p className="text-[14px] text-slate-400 text-center py-4">
                          {memberSearchResults.length === 0
                            ? "Никого не найдено"
                            : "Все найденные уже в списке"}
                        </p>
                      )}
                    {memberSearchQuery.length >= 2 &&
                      !isSearchingMembers &&
                      memberSearchResults.filter(
                        (acc) => !members.some((m) => m.account.id === acc.id),
                      ).length > 0 && (
                        <div className="space-y-1">
                          {memberSearchResults
                            .filter(
                              (acc) =>
                                !members.some((m) => m.account.id === acc.id),
                            )
                            .map((acc) => (
                              <button
                                key={acc.id}
                                type="button"
                                onClick={() => handleAddMember(acc)}
                                className="w-full flex items-center gap-3 min-h-[52px] px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 active:bg-slate-100 transition-colors text-left"
                              >
                                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                                  <IconUser className="w-4 h-4 text-slate-500" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 truncate text-[14px]">
                                    {formatAccountName(acc)}
                                  </p>
                                  {acc.username && (
                                    <p className="text-[12px] text-slate-500">
                                      @{acc.username}
                                    </p>
                                  )}
                                </div>
                              </button>
                            ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {editingField === "description" && (
          <FullscreenTextEditor
            isOpen={true}
            title="Описание"
            value={form.description}
            placeholder="Краткое описание служения..."
            onSave={(v) => {
              updateForm("description", v);
              setEditingField(null);
            }}
            onClose={() => setEditingField(null)}
          />
        )}

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="w-full py-3 rounded-xl bg-[var(--color-accent)] text-white font-semibold text-[15px] active:scale-[0.99] disabled:opacity-50"
          >
            {isSaving ? "Сохранение..." : "Сохранить"}
          </button>
          {!isNew && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full py-3 rounded-xl border border-red-200 text-red-600 font-semibold text-[15px] active:scale-[0.99] disabled:opacity-50"
            >
              {isDeleting ? "Удаление..." : "Удалить служение"}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
